import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../api/client.ts";
import type { Domain, Sender } from "../api/types.ts";
import { Preview } from "../components/composer/Preview.tsx";
import { useSendingIdentity } from "../components/SendingIdentity.tsx";

/** Shown as a placeholder, not prefilled: a prefilled body gets sent by mistake. */
const STARTER_HTML = `<p>Hi there,</p>
<p>This is a test message. Here is <a href="https://example.com">a link</a>.</p>`;

/**
 * A one-off message to a few addresses — checking how something renders in a
 * real inbox, or that a sender's DNS and Postal server work end to end — without
 * building a template and a campaign for it.
 */
export function TestSendPage() {
  const identity = useSendingIdentity();
  const senders = useQuery({
    queryKey: ["senders"],
    queryFn: () => api.get<Sender[]>("/config/senders"),
    retry: false,
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<Domain[]>("/config/domains"),
    retry: false,
  });

  /**
   * Narrowed to the company selected in the top bar. A sender belongs to a
   * domain, which belongs to one brand, so offering the others here would let
   * a test go out as a company the operator is not looking at.
   */
  const brandDomains = new Set(
    (domains.data ?? [])
      .filter((d) => !identity.brandId || d.brandId === identity.brandId)
      .map((d) => d.id),
  );
  const usable = (senders.data ?? []).filter((s) => s.isActive && brandDomains.has(s.domainId));

  // Follows the top-bar selection until picked here explicitly.
  const [chosenSender, setChosenSender] = useState<string | null>(null);
  const stillOffered = usable.some((s) => s.id === chosenSender);
  const senderId = (stillOffered ? chosenSender : null) ?? usable[0]?.id ?? "";

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("https://");
  const [result, setResult] = useState<string | null>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);

  const send = useMutation({
    mutationFn: () =>
      api.post<{ messageId: string; to: string[] }>("/test-send", {
        senderId,
        to: to
          .split(/[\s,;]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        subject,
        html,
        text,
      }),
    onSuccess: (res) => setResult(`Sent to ${res.to.join(", ")}. Postal message ${res.messageId}.`),
    onError: () => setResult(null),
  });

  /** Wraps the selection in a link, or inserts the URL as its own text. */
  function insertLink() {
    const el = htmlRef.current;
    const url = linkUrl.trim();
    if (!el || !url || url === "https://") return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const label = html.slice(start, end) || url;
    const tag = `<a href="${url.replaceAll('"', "&quot;")}">${label}</a>`;
    setHtml(html.slice(0, start) + tag + html.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + tag.length);
    });
  }

  function wrap(tagName: string) {
    const el = htmlRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const tag = `<${tagName}>${html.slice(start, end)}</${tagName}>`;
    setHtml(html.slice(0, start) + tag + html.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + tag.length);
    });
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Test send</h1>
        <p>
          A one-off message through a sender&rsquo;s Postal server. It isn&rsquo;t a campaign: it
          doesn&rsquo;t count against the sender&rsquo;s daily budget or show up in reports.
        </p>
      </div>

      {send.error && <div className="error">{send.error.message}</div>}
      {result && <div className="notice ok">{result}</div>}

      <div className="composer-grid">
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <div className="card-body">
            <div className="field">
              <label htmlFor="ts-from">From</label>
              <select
                id="ts-from"
                value={senderId}
                onChange={(e) => setChosenSender(e.target.value)}
              >
                {usable.length === 0 && <option value="">No senders configured</option>}
                {usable.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName} &lt;{s.email}&gt;
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="ts-to">To</label>
              <input
                id="ts-to"
                required
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="you@example.com"
              />
              <div className="hint">Up to five addresses, separated by commas.</div>
            </div>

            <StartFromTemplate
              onPick={(t) => {
                setSubject(t.subject);
                setHtml(t.compiledHtml ?? "");
                setText(t.compiledText ?? "");
              }}
            />

            <div className="field">
              <label htmlFor="ts-subject">Subject</label>
              <input
                id="ts-subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="ts-html">HTML body</label>
              <div className="palette">
                <button type="button" className="btn ghost small" onClick={() => wrap("strong")}>
                  Bold
                </button>
                <button type="button" className="btn ghost small" onClick={() => wrap("em")}>
                  Italic
                </button>
                <button type="button" className="btn ghost small" onClick={() => wrap("p")}>
                  Paragraph
                </button>
                <input
                  aria-label="Link URL"
                  style={{ flex: 1, minWidth: 160, width: "auto" }}
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      insertLink();
                    }
                  }}
                />
                <button type="button" className="btn ghost small" onClick={insertLink}>
                  + Link
                </button>
              </div>
              <textarea
                id="ts-html"
                ref={htmlRef}
                className="mono"
                rows={16}
                value={html}
                placeholder={STARTER_HTML}
                onChange={(e) => setHtml(e.target.value)}
              />
              <div className="hint">
                Select text, then add a link to wrap it. If the server tracks clicks, Postal
                rewrites links through its tracking domain.
              </div>
            </div>

            <div className="field">
              <label htmlFor="ts-text">Plain-text body</label>
              <textarea
                id="ts-text"
                rows={5}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Optional — generated from the HTML if left blank."
              />
            </div>

            <button className="btn" type="submit" disabled={send.isPending || !senderId}>
              {send.isPending ? "Sending…" : "Send test"}
            </button>
          </div>
        </form>

        <Preview
          html={html}
          text={text || (html.trim() ? "Generated from the HTML when sent." : "")}
        />
      </div>
    </div>
  );
}

interface TemplateOption {
  id: string;
  name: string;
}

/**
 * Loads a saved template's subject and compiled body to send as-is. Merge
 * fields stay as written here ({{first_name}}); "Send me a test" in the
 * composer fills them.
 */
function StartFromTemplate({
  onPick,
}: {
  onPick: (t: {
    subject: string;
    compiledHtml: string | null;
    compiledText: string | null;
  }) => void;
}) {
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateOption[]>("/templates"),
  });
  const load = useMutation({
    mutationFn: (id: string) =>
      api.get<{ subject: string; compiledHtml: string | null; compiledText: string | null }>(
        `/templates/${id}`,
      ),
    onSuccess: onPick,
  });
  if (!templates.data?.length) return null;
  return (
    <div className="field">
      <label htmlFor="ts-template">Start from a template</label>
      <select
        id="ts-template"
        defaultValue=""
        disabled={load.isPending}
        onChange={(e) => e.target.value && load.mutate(e.target.value)}
      >
        <option value="">Blank message</option>
        {templates.data.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <div className="hint">
        Merge fields are sent as written. To see them filled in, use Send me a test in the composer.
      </div>
    </div>
  );
}
